/**
 * Pays par défaut des numéros de l'ORGANISATION (`organizations.country`, migrations 0041 + 0042).
 *
 * C'est le pays proposé d'office dans les champs « numéro WhatsApp » : celui de l'organisation opératrice avant
 * toute connexion, et celui de l'organisation cliente pour ses membres connectés (`pays_defaut()`). Chaque
 * personne peut en choisir un autre : son choix est mémorisé sur son appareil.
 *
 * La liste des pays pris en charge vit dans le module partagé `otp-whatsapp` (et son jumeau Kotlin) : la base ne
 * stocke que le code ISO, et refuse (0042) tout code hors de la liste.
 *
 * ⚠️ COPIE à garder identique : `web/webapp-ts/src/api/paysOrganisation.ts` (portail SaaS).
 */
import { PAYS, PAYS_PAR_DEFAUT, trouverPays } from './commun'
import { supabase } from './supabase'

export interface OptionPays {
  valeur: string
  libelle: string
}

/** Options du sélecteur : « Mali (+223) », dans l'ordre alphabétique français. */
export function optionsPays(): OptionPays[] {
  return PAYS.map((p) => ({ valeur: p.iso, libelle: `${p.nom} (+${p.indicatif})` })).sort((a, b) =>
    a.libelle.localeCompare(b.libelle, 'fr'),
  )
}

/** Code ISO majuscule d'un pays de la table, sinon une erreur française (jamais d'écriture d'un code inconnu). */
export function validerPays(iso: string): string {
  const p = trouverPays(iso)
  if (!p) throw new Error("Ce pays n'est pas pris en charge pour les numéros WhatsApp.")
  return p.iso
}

/** Colonne absente (migration 0041 pas encore appliquée) : une base pas à jour, pas une panne. */
export function colonneAbsente(err: { code?: string; message?: string } | null | undefined): boolean {
  if (!err) return false
  const m = err.message ?? ''
  return (
    err.code === '42703' ||
    err.code === 'PGRST204' ||
    (/country/.test(m) && /does not exist|could not find|n'existe pas/i.test(m))
  )
}

/** Pays enregistré pour l'organisation (le pays de départ si la valeur est inconnue), ou `null` si la migration 0041 manque. */
export async function lirePaysOrganisation(orgId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('organizations')
    .select('country')
    .eq('id', orgId)
    .maybeSingle<{ country: string | null }>()
  if (error) {
    if (colonneAbsente(error)) return null
    throw new Error(error.message)
  }
  return trouverPays(data?.country)?.iso ?? PAYS_PAR_DEFAUT
}

export async function enregistrerPaysOrganisation(orgId: string, iso: string): Promise<void> {
  const code = validerPays(iso)
  const { data, error } = await supabase.from('organizations').update({ country: code }).eq('id', orgId).select('id')
  if (error) {
    if (colonneAbsente(error)) throw new Error('Réglage disponible après la mise à jour de la base (migration 0041).')
    if (error.code === '23514') throw new Error("Ce pays n'est pas pris en charge pour les numéros WhatsApp.")
    throw new Error(error.message)
  }
  /* Sous RLS, une mise à jour non autorisée ne lève PAS d'erreur : elle touche zéro ligne. */
  if (!data || data.length === 0) {
    throw new Error("Modification refusée : seul le propriétaire de l'organisation peut changer ce réglage.")
  }
}
